import { checkoutSuggestion } from "./checkout";
import type {
  GameState,
  MatchStats,
  PlayerStats,
  TeamStats,
  TurnRecord,
} from "./types";

const mean = (values: number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const median = (values: number[]) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const standardDeviation = (values: number[]) => {
  if (values.length < 2) return 0;
  const average = mean(values);
  return Math.sqrt(mean(values.map((value) => (value - average) ** 2)));
};

function turnsWithCurrent(state: GameState): TurnRecord[] {
  if (!state.currentTurn.length) return state.turns;
  return [
    ...state.turns,
    {
      id: "current",
      teamId: state.teams[state.activeTeam].id,
      playerId: state.activePlayerId,
      leg: state.leg,
      round: state.round,
      darts: state.currentTurn,
      score: state.currentTurn.reduce((sum, dart) => sum + dart.value, 0),
      bust: false,
      checkout: false,
    },
  ];
}

function checkoutOpportunityIds(state: GameState): Set<string> {
  const remaining = new Map<string, number>();
  const legByTeam = new Map<string, number>();
  const opportunities = new Set<string>();

  turnsWithCurrent(state).forEach((turn) => {
    if (legByTeam.get(turn.teamId) !== turn.leg) {
      legByTeam.set(turn.teamId, turn.leg);
      remaining.set(turn.teamId, state.config.startScore);
    }
    const scoreAtStart =
      turn.id === "current"
        ? state.currentTurnStart
        : (remaining.get(turn.teamId) ?? state.config.startScore);
    if (checkoutSuggestion(scoreAtStart, 3, state.config.doubleOut).length) {
      opportunities.add(turn.id);
    }
    if (!turn.bust) remaining.set(turn.teamId, Math.max(0, scoreAtStart - turn.score));
  });

  return opportunities;
}

function statsForTurns(
  state: GameState,
  turns: TurnRecord[],
  opportunityIds: Set<string>,
): PlayerStats {
  const darts = turns.flatMap((turn) => turn.darts);
  const scoredDarts = turns.flatMap((turn) =>
    turn.darts.map((dart) => (turn.bust ? 0 : dart.value)),
  );
  const total = turns.reduce((sum, turn) => sum + turn.score, 0);
  const firstNine = scoredDarts.slice(0, 9);
  const checkoutTurns = turns.filter((turn) => turn.checkout);
  const completedTurnScores = turns.map((turn) => turn.score);
  const nonBustScores = turns.filter((turn) => !turn.bust).map((turn) => turn.score);
  const checkoutAttempts = turns.filter((turn) => opportunityIds.has(turn.id)).length;
  const doubles = darts.filter((dart) => dart.multiplier === 2 && dart.segment > 0);
  const doubleCheckoutHits = checkoutTurns.filter(
    (turn) => turn.darts.at(-1)?.multiplier === 2,
  ).length;
  const winningLegDarts = checkoutTurns.map((checkoutTurn) =>
    turns
      .filter((turn) => turn.leg === checkoutTurn.leg)
      .reduce((sum, turn) => sum + turn.darts.length, 0),
  );
  const band = (minimum: number, maximum: number) =>
    completedTurnScores.filter((score) => score >= minimum && score <= maximum).length;

  return {
    darts: darts.length,
    totalPoints: total,
    turns: turns.length,
    average: darts.length ? (total / darts.length) * 3 : 0,
    firstNine: firstNine.length ? mean(firstNine) * 3 : 0,
    averagePerTurn: mean(completedTurnScores),
    highestTurn: Math.max(0, ...completedTurnScores),
    lowestTurn: nonBustScores.length ? Math.min(...nonBustScores) : 0,
    medianTurn: median(completedTurnScores),
    consistency: standardDeviation(completedTurnScores),
    checkoutHigh: Math.max(0, ...checkoutTurns.map((turn) => turn.score)),
    checkoutAttempts,
    checkoutRate: checkoutAttempts ? (checkoutTurns.length / checkoutAttempts) * 100 : 0,
    checkouts: checkoutTurns.length,
    doublesAttempted: doubles.length,
    doublesHit: doubleCheckoutHits,
    doubleRate: doubles.length ? (doubleCheckoutHits / doubles.length) * 100 : 0,
    busts: turns.filter((turn) => turn.bust).length,
    sixties: band(60, 79),
    eighties: band(80, 99),
    hundreds: band(100, 119),
    oneTwenties: band(120, 139),
    oneForties: band(140, 159),
    oneSixties: band(160, 179),
    oneEighties: completedTurnScores.filter((score) => score === 180).length,
    averageDartsPerWonLeg: mean(winningLegDarts),
    hitBreakdown: {
      singles: darts.filter((dart) => dart.multiplier === 1 && dart.segment !== 25).length,
      doubles: darts.filter((dart) => dart.multiplier === 2 && dart.segment !== 25).length,
      triples: darts.filter((dart) => dart.multiplier === 3).length,
      bull25: darts.filter((dart) => dart.segment === 25 && dart.multiplier === 1).length,
      bull50: darts.filter((dart) => dart.segment === 25 && dart.multiplier === 2).length,
      misses: darts.filter((dart) => dart.segment === 0).length,
    },
    scoreBands: [
      completedTurnScores.filter((score) => score < 40).length,
      band(40, 59),
      band(60, 79),
      band(80, 99),
      band(100, 139),
      band(140, 179),
      completedTurnScores.filter((score) => score === 180).length,
    ],
    turnScores: completedTurnScores,
  };
}

export function playerStats(state: GameState, playerId: string): PlayerStats {
  const turns = turnsWithCurrent(state).filter((turn) => turn.playerId === playerId);
  return statsForTurns(state, turns, checkoutOpportunityIds(state));
}

export function teamStats(state: GameState, teamId: string): TeamStats {
  const allTurns = turnsWithCurrent(state);
  const turns = allTurns.filter((turn) => turn.teamId === teamId);
  const opportunities = checkoutOpportunityIds(state);
  const base = statsForTurns(state, turns, opportunities);
  const team = state.teams.find((item) => item.id === teamId);
  const contributions = (team?.playerIds ?? []).map((playerId) => {
    const member = playerStats(state, playerId);
    return {
      playerId,
      pointsPercent: base.totalPoints ? (member.totalPoints / base.totalPoints) * 100 : 0,
      dartsPercent: base.darts ? (member.darts / base.darts) * 100 : 0,
      checkoutPercent: base.checkouts ? (member.checkouts / base.checkouts) * 100 : 0,
    };
  });

  return {
    darts: base.darts,
    totalPoints: base.totalPoints,
    turns: base.turns,
    average: base.average,
    firstNine: base.firstNine,
    highestTurn: base.highestTurn,
    checkoutHigh: base.checkoutHigh,
    checkoutAttempts: base.checkoutAttempts,
    checkoutRate: base.checkoutRate,
    checkouts: base.checkouts,
    busts: base.busts,
    hundredsPlus:
      base.hundreds + base.oneTwenties + base.oneForties + base.oneSixties + base.oneEighties,
    oneFortiesPlus: base.oneForties + base.oneSixties + base.oneEighties,
    oneEighties: base.oneEighties,
    contributions,
  };
}

export function matchStats(state: GameState): MatchStats {
  const playerRows = state.players.map((player) => ({
    id: player.id,
    stats: playerStats(state, player.id),
  }));
  const teamRows = state.teams.map((team) => ({
    id: team.id,
    stats: teamStats(state, team.id),
  }));
  const totalDarts = playerRows.reduce((sum, item) => sum + item.stats.darts, 0);
  const totalPoints = playerRows.reduce((sum, item) => sum + item.stats.totalPoints, 0);
  const averageLeader = [...playerRows].sort((a, b) => b.stats.average - a.stats.average)[0];
  const checkoutLeader = [...playerRows].sort(
    (a, b) => b.stats.checkoutHigh - a.stats.checkoutHigh,
  )[0];
  const teamAverageLeader = [...teamRows].sort((a, b) => b.stats.average - a.stats.average)[0];
  const teamCheckoutLeader = [...teamRows].sort(
    (a, b) => b.stats.checkoutHigh - a.stats.checkoutHigh,
  )[0];

  return {
    durationMs: Math.max(0, state.updatedAt - state.createdAt),
    totalDarts,
    totalTurns: playerRows.reduce((sum, item) => sum + item.stats.turns, 0),
    overallAverage: totalDarts ? (totalPoints / totalDarts) * 3 : 0,
    totalBusts: playerRows.reduce((sum, item) => sum + item.stats.busts, 0),
    total180s: playerRows.reduce((sum, item) => sum + item.stats.oneEighties, 0),
    averageLeaderId: averageLeader?.id,
    checkoutLeaderId: checkoutLeader?.stats.checkoutHigh ? checkoutLeader.id : undefined,
    teamAverageLeaderId: teamAverageLeader?.id,
    teamCheckoutLeaderId: teamCheckoutLeader?.stats.checkoutHigh
      ? teamCheckoutLeader.id
      : undefined,
  };
}
